import React, { useState, useEffect } from 'react';
// import { useTheme } from '../contexts/ThemeContext';
import AppSettingsService from '../services/appSettingsService';
import { formatDuration } from '../utils/timeUtils';
import './QuizAgreement.css';

const DEFAULT_QUIZ_INSTRUCTIONS = {
  title: 'Online Quiz Instructions & Agreement',
  estimated_time_label: 'Estimated Time:',
  estimated_time_value: '1.45 mins',
  total_marks_label: 'Total Marks:',
  total_marks_value: '100',
  breakdown_label: 'Breakdown:',
  breakdown_text: '{common_percentage}% Common Section, {role_specific_percentage}% role-Specific',
  intro_text: 'Dear TCSC26 candidates, welcome to online quiz part of program. This is a comprehensive assessment designed to evaluate your overall conceptual understanding across multiple areas. The exam consists of both a common section and a department specific section based on role you have applied for. You are encouraged to carefully read each question, think critically, and respond to the best of your ability.',
  agreement_text: 'I have read and agree to all the above instructions and examination rules.',
  error_text: 'You must agree to instructions before starting the quiz.',
  sections: [
    {
      title: '1. Examination Structure',
      body: 'The examination will consist of two sections:',
      bullets: [
        'A Common Section (applicable to all candidates)',
        'A Department-Specific Section based on role applied for [.NET Web Development or Data, AI & Python or Microsoft Power Platform Developer]',
      ],
      variant: 'normal',
    },
    {
      title: '2. Question Format',
      body: 'The question format will include multiple choice, short-answer, descriptive, and coding questions.',
      bullets: [],
      variant: 'normal',
    },
    {
      title: '3. Marking Scheme',
      body: 'The total marks for examination will be {total_marks}.',
      bullets: [
        '{common_percentage}% of marks will come from Common Section',
        '{role_specific_percentage}% of marks will come from Department-Specific Section',
      ],
      variant: 'normal',
    },
    {
      title: '4. Time Duration',
      body: 'The quiz is time bound. The total duration of exam will be shown above.',
      bullets: [],
      variant: 'important',
    },
    {
      title: '5. Tab Focus Requirement',
      body: 'The quiz tab must remain in focus at all times during exam. The system can detect tab switching and repeated violations may terminate the exam session.',
      bullets: [],
      variant: 'critical',
    },
  ],
};

const safeInstructions = (data) => {
  if (!data || typeof data !== 'object') return DEFAULT_QUIZ_INSTRUCTIONS;

  return {
    ...DEFAULT_QUIZ_INSTRUCTIONS,
    ...data,
    sections: Array.isArray(data.sections) && data.sections.length
      ? data.sections
      : DEFAULT_QUIZ_INSTRUCTIONS.sections,
  };
};

const LEGACY_BREAKDOWN_TEXT = '70% Common Section, 30% role-Specific';

const normalizeInstructionText = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const isLegacyBreakdownText = (text) =>
  normalizeInstructionText(text) === normalizeInstructionText(LEGACY_BREAKDOWN_TEXT);

const getQuestionPoints = (question) => {
  const points = Number(question?.points || 0);
  return Number.isFinite(points) ? points : 0;
};

const getQuestionDesignation = (question) =>
  question?.designation || question?.designationId || question?.designation_id || null;

const getTotalMarks = (quizData, fallback) => {
  const questions = Array.isArray(quizData?.questions) ? quizData.questions : [];
  const questionPoints = questions.reduce((total, question) => total + getQuestionPoints(question), 0);

  return questionPoints || quizData?.total_marks || fallback;
};

const getQuizSummary = (quizData, instructions) => {
  const questions = Array.isArray(quizData?.questions) ? quizData.questions : [];
  const questionPoints = questions.reduce((total, question) => total + getQuestionPoints(question), 0);
  const commonPoints = questions.reduce((total, question) => {
    return getQuestionDesignation(question) ? total : total + getQuestionPoints(question);
  }, 0);
  const roleSpecificPoints = Math.max(questionPoints - commonPoints, 0);
  const totalMarks = getTotalMarks(quizData, instructions.total_marks_value);
  const estimatedTime = quizData?.total_time
    ? formatDuration(quizData.total_time)
    : instructions.estimated_time_value;

  const hasQuestionPoints = questionPoints > 0;
  const commonPercentage = hasQuestionPoints
    ? Math.round((commonPoints / questionPoints) * 100)
    : 70;
  const roleSpecificPercentage = Math.max(100 - commonPercentage, 0);
  const breakdownText = `${commonPercentage}% Common Section, ${roleSpecificPercentage}% role-Specific`;

  return {
    totalMarks,
    estimatedTime,
    commonPoints,
    roleSpecificPoints,
    commonPercentage,
    roleSpecificPercentage,
    breakdownText,
  };
};

const applyDynamicInstructionText = (text, summary) => {
  if (!text) return text;

  const replacements = {
    total_marks: summary.totalMarks,
    estimated_time: summary.estimatedTime,
    common_marks: summary.commonPoints,
    role_specific_marks: summary.roleSpecificPoints,
    common_percentage: summary.commonPercentage,
    role_specific_percentage: summary.roleSpecificPercentage,
    breakdown: summary.breakdownText,
  };

  let output = String(text).replace(/\{([a-z_]+)\}/gi, (match, key) => {
    const value = replacements[key.toLowerCase()];
    return value === null || value === undefined || value === '' ? match : value;
  });

  if (summary.totalMarks !== null && summary.totalMarks !== undefined) {
    output = output.replace(
      /The total marks for examination will be\s+\d+\.?/i,
      `The total marks for examination will be ${summary.totalMarks}.`
    );
  }

  if (summary.commonPercentage !== null && summary.roleSpecificPercentage !== null) {
    output = output
      .replace(/70%\s+of marks will come from Common Section/i, `${summary.commonPercentage}% of marks will come from Common Section`)
      .replace(/30%\s+of marks will come from Department-Specific Section/i, `${summary.roleSpecificPercentage}% of marks will come from Department-Specific Section`);
  }

  return output;
};

const QuizAgreement = ({ onStartQuiz, quizData, hasAgreed, hasStarted, isSubmitted }) => {
  // const { theme } = useTheme();
  const [agreed, setAgreed] = useState(hasAgreed || false);
  const [showError, setShowError] = useState(false);
  const [instructions, setInstructions] = useState(DEFAULT_QUIZ_INSTRUCTIONS);

  useEffect(() => {
    let isMounted = true;

    const loadInstructions = async () => {
      const result = await AppSettingsService.getQuizInstructions();
      if (isMounted && result.success) {
        setInstructions(safeInstructions(result.data));
      }
    };

    loadInstructions();

    return () => {
      isMounted = false;
    };
  }, []);

  // If user has already agreed or quiz has started, proceed directly
  useEffect(() => {
    if (hasAgreed || hasStarted || isSubmitted) {
      onStartQuiz({ agreed: true, timestamp: new Date().toISOString() });
    }
  }, [hasAgreed, hasStarted, isSubmitted, onStartQuiz]);

  const handleStartQuiz = () => {
    if (!agreed) {
      setShowError(true);
      return;
    }
    
    onStartQuiz({
      agreed: true,
      timestamp: new Date().toISOString(),
      agreementConfirmed: true
    });
  };

  const handleBack = () => {
    window.history.back();
  };

  const quizSummary = getQuizSummary(quizData, instructions);
  const breakdownValue = isLegacyBreakdownText(instructions.breakdown_text)
    ? quizSummary.breakdownText
    : applyDynamicInstructionText(instructions.breakdown_text, quizSummary);

  const renderText = (text, className = '') => {
    if (!text) return null;

    return applyDynamicInstructionText(text, quizSummary)
      .split(/\n+/)
      .filter((line) => line.trim())
      .map((line, index) => (
        <p key={`${line}-${index}`} className={className}>
          {line}
        </p>
      ));
  };

  // If already agreed or started, don't show agreement screen
  if (hasAgreed || hasStarted || isSubmitted) {
    return null;
  }

  return (
    <div className="quiz-agreement-page">
      <div className="agreement-container">
        <header className="agreement-header">
          <h1>{instructions.title}</h1>
          <div className="quiz-meta">
            <div className="meta-item">
              <span className="meta-label">{instructions.estimated_time_label}</span>
              <span className="meta-value">
                {quizSummary.estimatedTime}
              </span>
            </div>
            <div className="meta-item">
              <span className="meta-label">{instructions.total_marks_label}</span>
              <span className="meta-value">{quizSummary.totalMarks}</span>
            </div>
            <div className="meta-item">
              <span className="meta-label">{instructions.breakdown_label}</span>
              <span className="meta-value">{breakdownValue}</span>
            </div>
          </div>
        </header>

        <div className="agreement-content">
          <div className="instruction-text">
            <div className="intro-text">
              {renderText(instructions.intro_text)}
            </div>

            <div className="numbered-sections">
              {instructions.sections.map((section, index) => {
                const variant = ['important', 'critical'].includes(section.variant)
                  ? section.variant
                  : '';

                return (
                  <div key={`${section.title}-${index}`} className={`section-item ${variant}`}>
                    {section.title && <h3>{section.title}</h3>}
                    {renderText(section.body, section.variant === 'critical' ? 'warning-text' : '')}
                    {Array.isArray(section.bullets) && section.bullets.length > 0 && (
                      <ul>
                        {section.bullets.map((bullet, bulletIndex) => (
                          <li key={`${bullet}-${bulletIndex}`}>
                            {applyDynamicInstructionText(bullet, quizSummary)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="agreement-actions">
          {showError && (
            <div className="error-message">
              {instructions.error_text}
            </div>
          )}
          
          <div className="agreement-checkbox">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => {
                  setAgreed(e.target.checked);
                  setShowError(false);
                }}
              />
              <span className="checkmark"></span>
              {instructions.agreement_text}
            </label>
          </div>

          <div className="action-buttons">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleBack}
            >
              Back
            </button>
            
            <button
              type="button"
              className={`btn-primary ${!agreed ? 'disabled' : ''}`}
              onClick={handleStartQuiz}
              disabled={!agreed}
            >
              Start Quiz
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizAgreement;
